import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router";
import {
  RefreshCw, CheckCircle, XCircle, Clock, LogOut,
  ChevronDown, Users, Server, Mail, Archive, Menu, X, Inbox,
  Phone, Trash2, LayoutDashboard, Eye, FileText, Bell,
  ShieldCheck, Shield, ShieldAlert,
  BarChart2, BookOpen, AlertTriangle, AlertCircle, ClipboardList, ExternalLink,
} from "lucide-react";
import { ISOModule, type ISOTab } from "./ISOPage";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";
import { Logo } from "../components/Logo";

interface AccessRequest {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  isBusiness?: boolean;
  businessName?: string | null;
  ndaAccepted: boolean;
  token: string;
  status: "pending" | "approved" | "rejected";
  createdAt: string;
  archived?: boolean;
  statusStage?: string;
  ndaVersion?:       string;
  ndaPdfLink?:       string | null;
  signature?:        string | null;
  ipAddress?:        string | null;
  sessionExpiresAt?: string | null;
}

interface InfrastructureRequest {
  id: string;
  name: string;
  company: string;
  email: string;
  phone: string;
  type: string;
  message: string;
  status: string;
  createdAt: string;
  archived?: boolean;
  statusStage?: string;
}

interface ContactRequest {
  id: string;
  name: string;
  company: string;
  email: string;
  phone: string;
  service: string;
  message: string;
  ndaAccepted?: boolean;
  createdAt: string;
  archived?: boolean;
  statusStage?: string;
}

type Tab        = "dashboard" | "access" | "infrastructure" | "contact" | "archived"
                | "iso-dashboard" | "iso-documents" | "iso-controls" | "iso-risks" | "iso-incidents" | "iso-audits";
type RecordType = "access" | "infrastructure" | "contact";
type Stage      = "Prospect" | "Meeting" | "Deal";

type ArchivedRecord =
  | (AccessRequest         & { _type: "access" })
  | (InfrastructureRequest & { _type: "infrastructure" })
  | (ContactRequest        & { _type: "contact" });

// ─── Status Badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: AccessRequest["status"] }) {
  const map = {
    pending:  { label: "Pending",  classes: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30" },
    approved: { label: "Approved", classes: "bg-green-500/15 text-green-400 border-green-500/30" },
    rejected: { label: "Rejected", classes: "bg-red-500/15 text-red-400 border-red-500/30" },
  };
  const { label, classes } = map[status];
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${classes}`}>
      {label}
    </span>
  );
}

// ─── Stage Badge ──────────────────────────────────────────────────────────────

function StageBadge({ stage }: { stage?: string }) {
  const s = stage || "Prospect";
  const map: Record<string, string> = {
    Prospect: "bg-white/8 text-white/50 border-white/15",
    Meeting:  "bg-blue-500/15 text-blue-400 border-blue-500/30",
    Deal:     "bg-green-500/15 text-green-400 border-green-500/30",
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${map[s] ?? map["Prospect"]}`}>
      {s}
    </span>
  );
}

// ─── Type Badge ───────────────────────────────────────────────────────────────

function TypeBadge({ isBusiness }: { isBusiness?: boolean }) {
  return isBusiness
    ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border bg-blue-500/15 text-blue-400 border-blue-500/30">Business</span>
    : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border bg-white/8 text-white/50 border-white/15">Individual</span>;
}

// ─── Access Actions Panel (Right Drawer) ─────────────────────────────────────

function AccessActionsPanel({
  record,
  onClose,
  approve,
  reject,
  archive,
  extendSession,
  accessLinks,
  onViewNDA,
  actionLoading,
}: {
  record:          AccessRequest | null;
  onClose:         () => void;
  approve:         (id: string) => void;
  reject:          (id: string) => void;
  archive:         (id: string) => void;
  extendSession:   (id: string) => void;
  accessLinks:     Record<string, string>;
  onViewNDA:       (id: string) => void;
  actionLoading:   string | null;
}) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (record) {
      requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
    }
  }, [record]);

  useEffect(() => {
    if (!record) return;
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [record, onClose]);

  if (!record) return null;

  const link = accessLinks[record.id] || (record.status === "approved" ? `${window.location.origin}/access/${record.token}` : null);
  const busy = !!actionLoading;

  function openEmailDraft() {
    const subject = encodeURIComponent("LEO Group \u2013 Access & Introduction");
    const body = encodeURIComponent(
      `Hello ${record!.firstName},\n\nThank you for your interest in LEO Group.\n\nWe would like to schedule a meeting with you to better understand your requirements.\n\nIn the meantime, you can view our online profile using the link below:\n\n${link ?? "[ACCESS LINK]"}\n\nKind regards,\nLEO Group`
    );
    window.open(`mailto:${record!.email}?subject=${subject}&body=${body}`);
    onClose();
  }

  return (
    <div className="fixed inset-0 z-[200] flex justify-end">
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black/60 transition-opacity duration-200 ${visible ? "opacity-100" : "opacity-0"}`}
        onClick={onClose}
      />

      {/* Drawer panel */}
      <div className={`relative flex flex-col w-full sm:w-[400px] h-full bg-[#0D1520] border-l border-white/10 shadow-[−8px_0_40px_rgba(0,0,0,0.6)] transition-transform duration-300 ease-out ${visible ? "translate-x-0" : "translate-x-full"}`}>

        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-white/8">
          <div className="min-w-0">
            <p className="text-[10px] text-white/30 uppercase tracking-widest mb-1">Access Request</p>
            <h2 className="text-sm font-semibold text-white leading-snug">{record.firstName} {record.lastName}</h2>
            <div className="flex items-center gap-1.5 mt-2 flex-wrap">
              <StatusBadge status={record.status} />
              <TypeBadge isBusiness={record.isBusiness} />
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-white/30 hover:text-white hover:bg-white/8 rounded-lg transition-colors flex-shrink-0 mt-0.5"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Client info */}
        <div className="px-5 py-4 border-b border-white/8 space-y-3">
          <div className="flex items-center gap-3">
            <Mail className="w-3.5 h-3.5 text-white/25 flex-shrink-0" />
            <span className="text-xs text-white/65 break-all">{record.email}</span>
          </div>
          <div className="flex items-center gap-3">
            <Phone className="w-3.5 h-3.5 text-white/25 flex-shrink-0" />
            <span className="text-xs text-white/65">{record.phone}</span>
          </div>
          {record.isBusiness && record.businessName && (
            <div className="flex items-center gap-3">
              <Server className="w-3.5 h-3.5 text-white/25 flex-shrink-0" />
              <span className="text-xs text-white/65">{record.businessName}</span>
            </div>
          )}
          <div className="flex items-center gap-3">
            <Clock className="w-3.5 h-3.5 text-white/25 flex-shrink-0" />
            <span className="text-xs text-white/40">
              Submitted {new Date(record.createdAt).toLocaleDateString("en-ZA", { day: "2-digit", month: "long", year: "numeric" })}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-2.5">
          <p className="text-[10px] text-white/25 uppercase tracking-widest mb-4">Actions</p>

          {record.status !== "approved" && (
            <button
              disabled={busy}
              onClick={() => { approve(record.id); onClose(); }}
              className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl bg-green-500/10 hover:bg-green-500/16 border border-green-500/20 hover:border-green-500/30 text-green-400 text-sm font-medium transition-colors disabled:opacity-40"
            >
              <CheckCircle className="w-4 h-4 flex-shrink-0" />
              Approve Request
            </button>
          )}

          {record.status !== "rejected" && (
            <button
              disabled={busy}
              onClick={() => { reject(record.id); onClose(); }}
              className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl bg-red-500/10 hover:bg-red-500/16 border border-red-500/20 hover:border-red-500/30 text-red-400 text-sm font-medium transition-colors disabled:opacity-40"
            >
              <XCircle className="w-4 h-4 flex-shrink-0" />
              Reject Request
            </button>
          )}

          {link && (
            <button
              onClick={openEmailDraft}
              className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl bg-white/5 hover:bg-white/8 border border-white/10 hover:border-white/18 text-white/65 hover:text-white/80 text-sm font-medium transition-colors"
            >
              <Mail className="w-4 h-4 flex-shrink-0" />
              Send Access Link
            </button>
          )}

          {record.status === "approved" && (
            <button
              disabled={busy}
              onClick={() => { extendSession(record.id); onClose(); }}
              className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl bg-[#7B1A1A]/15 hover:bg-[#7B1A1A]/25 border border-[#7B1A1A]/30 hover:border-[#7B1A1A]/50 text-red-300 text-sm font-medium transition-colors disabled:opacity-40"
            >
              <RefreshCw className="w-4 h-4 flex-shrink-0" />
              Extend Session +15 min
            </button>
          )}

          {record.status === "approved" && record.sessionExpiresAt && (
            <p className="text-[10px] text-white/25 px-1">
              Session expires: {new Date(record.sessionExpiresAt).toLocaleTimeString("en-ZA", { hour: "2-digit", minute: "2-digit" })}
            </p>
          )}

          <button
            onClick={() => { onViewNDA(record.id); onClose(); }}
            className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl bg-[#7B9FFF]/10 hover:bg-[#7B9FFF]/16 border border-[#7B9FFF]/20 hover:border-[#7B9FFF]/30 text-[#7B9FFF] text-sm font-medium transition-colors"
          >
            <Eye className="w-4 h-4 flex-shrink-0" />
            View NDA Record
          </button>

          <div className="border-t border-white/8 my-1" />

          <button
            disabled={busy}
            onClick={() => { archive(record.id); onClose(); }}
            className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl bg-yellow-500/8 hover:bg-yellow-500/12 border border-yellow-500/15 hover:border-yellow-500/25 text-yellow-400 text-sm font-medium transition-colors disabled:opacity-40"
          >
            <Archive className="w-4 h-4 flex-shrink-0" />
            Archive Record
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Lead Actions Panel (Infrastructure + Contact) ───────────────────────────

type LeadPanelRecord = (InfrastructureRequest | ContactRequest) & { _type: "infrastructure" | "contact" };

function LeadActionsPanel({
  record,
  onClose,
  onSetStage,
  onArchive,
}: {
  record:     LeadPanelRecord | null;
  onClose:    () => void;
  onSetStage: (id: string, recordType: string, stage: Stage) => void;
  onArchive:  (id: string, recordType: string) => void;
}) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (record) {
      requestAnimationFrame(() => setVisible(true));
    } else {
      setVisible(false);
    }
  }, [record]);

  useEffect(() => {
    if (!record) return;
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [record, onClose]);

  if (!record) return null;

  const stageMeta: Record<Stage, { color: string; border: string }> = {
    Prospect: { color: "text-white/65",  border: "border-white/10 bg-white/5 hover:bg-white/8" },
    Meeting:  { color: "text-blue-400",  border: "border-blue-500/20 bg-blue-500/10 hover:bg-blue-500/16" },
    Deal:     { color: "text-green-400", border: "border-green-500/20 bg-green-500/10 hover:bg-green-500/16" },
  };

  return (
    <div className="fixed inset-0 z-[200] flex justify-end">
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black/60 transition-opacity duration-200 ${visible ? "opacity-100" : "opacity-0"}`}
        onClick={onClose}
      />

      {/* Drawer panel */}
      <div className={`relative flex flex-col w-full sm:w-[400px] h-full bg-[#0D1520] border-l border-white/10 shadow-[−8px_0_40px_rgba(0,0,0,0.6)] transition-transform duration-300 ease-out ${visible ? "translate-x-0" : "translate-x-full"}`}>

        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-white/8">
          <div className="min-w-0">
            <p className="text-[10px] text-white/30 uppercase tracking-widest mb-1">
              {record._type === "infrastructure" ? "Infrastructure Request" : "Contact Request"}
            </p>
            <h2 className="text-sm font-semibold text-white leading-snug">{record.name}</h2>
            <div className="flex items-center gap-1.5 mt-2">
              <StageBadge stage={record.statusStage} />
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-white/30 hover:text-white hover:bg-white/8 rounded-lg transition-colors flex-shrink-0 mt-0.5"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Client info */}
        <div className="px-5 py-4 border-b border-white/8 space-y-3">
          <div className="flex items-center gap-3">
            <Mail className="w-3.5 h-3.5 text-white/25 flex-shrink-0" />
            <span className="text-xs text-white/65 break-all">{record.email}</span>
          </div>
          {record.phone && (
            <div className="flex items-center gap-3">
              <Phone className="w-3.5 h-3.5 text-white/25 flex-shrink-0" />
              <span className="text-xs text-white/65">{record.phone}</span>
            </div>
          )}
          {record.company && (
            <div className="flex items-center gap-3">
              <Server className="w-3.5 h-3.5 text-white/25 flex-shrink-0" />
              <span className="text-xs text-white/65">{record.company}</span>
            </div>
          )}
          <div className="flex items-center gap-3">
            <Clock className="w-3.5 h-3.5 text-white/25 flex-shrink-0" />
            <span className="text-xs text-white/40">
              Submitted {new Date(record.createdAt).toLocaleDateString("en-ZA", { day: "2-digit", month: "long", year: "numeric" })}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-2.5">
          <p className="text-[10px] text-white/25 uppercase tracking-widest mb-4">Actions</p>

          <a
            href={`mailto:${record.email}?subject=${encodeURIComponent("LEO Group Enquiry")}`}
            onClick={onClose}
            className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl bg-white/5 hover:bg-white/8 border border-white/10 hover:border-white/18 text-white/65 hover:text-white/80 text-sm font-medium transition-colors"
          >
            <Mail className="w-4 h-4 flex-shrink-0" />
            Contact Client
          </a>

          {record.phone && (
            <a
              href={`tel:${record.phone}`}
              onClick={onClose}
              className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl bg-green-500/10 hover:bg-green-500/16 border border-green-500/20 hover:border-green-500/30 text-green-400 text-sm font-medium transition-colors"
            >
              <Phone className="w-4 h-4 flex-shrink-0" />
              Call Client
            </a>
          )}

          <div className="border-t border-white/8 my-1" />
          <p className="text-[10px] text-white/20 uppercase tracking-widest pb-1">Pipeline Stage</p>

          {(["Prospect", "Meeting", "Deal"] as Stage[]).map((stage) => (
            <button
              key={stage}
              onClick={() => { onSetStage(record.id, record._type, stage); onClose(); }}
              className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border text-sm font-medium transition-colors ${stageMeta[stage].color} ${stageMeta[stage].border}`}
            >
              <CheckCircle className="w-4 h-4 flex-shrink-0 opacity-70" />
              Set as {stage}
            </button>
          ))}

          <div className="border-t border-white/8 my-1" />

          <button
            onClick={() => { onArchive(record.id, record._type); onClose(); }}
            className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl bg-yellow-500/8 hover:bg-yellow-500/12 border border-yellow-500/15 hover:border-yellow-500/25 text-yellow-400 text-sm font-medium transition-colors"
          >
            <Archive className="w-4 h-4 flex-shrink-0" />
            Archive Record
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Access Requests Table ────────────────────────────────────────────────────

function AccessTable({
  rows,
  loading,
  emptyLabel,
  onOpenActions,
}: {
  rows:          AccessRequest[];
  loading:       boolean;
  emptyLabel:    string;
  onOpenActions: (r: AccessRequest) => void;
}) {
  if (loading && rows.length === 0) {
    return <div className="px-6 py-16 text-center text-white/30 text-sm">Loading…</div>;
  }
  if (rows.length === 0) {
    return <div className="px-6 py-16 text-center text-white/30 text-sm">{emptyLabel}</div>;
  }

  return (
    <>
      {/* Mobile cards */}
      <div className="md:hidden divide-y divide-white/5">
        {rows.map((r) => (
          <div key={r.id} className="p-4 space-y-3">
            <div className="flex items-start justify-between gap-2">
              <p className="text-white font-medium text-sm">{r.firstName} {r.lastName}</p>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <TypeBadge isBusiness={r.isBusiness} />
                <StatusBadge status={r.status} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div><p className="text-white/40 mb-0.5">Email</p><p className="text-white/80 break-all">{r.email}</p></div>
              <div><p className="text-white/40 mb-0.5">Phone</p><p className="text-white/80">{r.phone}</p></div>
              {r.isBusiness && r.businessName && (
                <div className="col-span-2"><p className="text-white/40 mb-0.5">Business</p><p className="text-white/80">{r.businessName}</p></div>
              )}
              <div>
                <p className="text-white/40 mb-0.5">NDA Accepted</p>
                <p className={r.ndaAccepted ? "text-green-400" : "text-red-400"}>{r.ndaAccepted ? "Yes" : "No"}</p>
              </div>
              <div>
                <p className="text-white/40 mb-0.5">Submitted</p>
                <p className="text-white/60">{new Date(r.createdAt).toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" })}</p>
              </div>
              {r.ndaPdfLink && (
                <div className="col-span-2">
                  <a href={`/api/admin/nda/${r.ndaPdfLink}`} target="_blank" rel="noopener noreferrer" className="text-xs text-[#7B9FFF] hover:underline">Download Signed NDA</a>
                </div>
              )}
            </div>
            <div className="pt-1 border-t border-white/10">
              <button
                onClick={() => onOpenActions(r)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/8 hover:bg-white/12 border border-white/15 text-white/80 text-xs rounded transition-colors"
              >
                Actions <ChevronDown className="w-3 h-3" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10 text-left">
              {["Name", "Type", "Business Name", "Email", "Phone", "NDA", "Status", "Submitted", ""].map((h) => (
                <th key={h} className="px-4 py-3 text-xs font-semibold text-white/30 uppercase tracking-wide whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-white/3 transition-colors">
                <td className="px-4 py-3 font-medium text-white whitespace-nowrap">{r.firstName} {r.lastName}</td>
                <td className="px-4 py-3"><TypeBadge isBusiness={r.isBusiness} /></td>
                <td className="px-4 py-3 text-white/60 text-xs whitespace-nowrap">
                  {r.businessName ?? <span className="text-white/20">—</span>}
                </td>
                <td className="px-4 py-3 text-white/60 text-xs">{r.email}</td>
                <td className="px-4 py-3 text-white/60 text-xs whitespace-nowrap">{r.phone}</td>
                <td className="px-4 py-3">
                  <div className="flex flex-col gap-0.5">
                    <span className={r.ndaAccepted ? "text-green-400 text-xs" : "text-red-400 text-xs"}>
                      {r.ndaAccepted ? "Accepted" : "Not accepted"}
                    </span>
                    {r.ndaVersion && <span className="text-white/30 text-[10px]">{r.ndaVersion}</span>}
                  </div>
                </td>
                <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                <td className="px-4 py-3 text-white/40 whitespace-nowrap text-xs">
                  {new Date(r.createdAt).toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" })}
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => onOpenActions(r)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/8 hover:bg-white/12 border border-white/15 text-white/80 text-xs rounded transition-colors whitespace-nowrap"
                  >
                    Actions <ChevronDown className="w-3 h-3" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ─── Combined analytics helper ────────────────────────────────────────────────

function getCombined30Days(
  access:  { createdAt: string }[],
  infra:   { createdAt: string }[],
  contact: { createdAt: string }[],
) {
  const map: Record<string, { access: number; infra: number; contact: number }> = {};
  const now = new Date();
  for (let i = 29; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    map[d.toISOString().slice(0, 10)] = { access: 0, infra: 0, contact: 0 };
  }
  for (const r of access)  { const k = r.createdAt.slice(0, 10); if (k in map) map[k].access++; }
  for (const r of infra)   { const k = r.createdAt.slice(0, 10); if (k in map) map[k].infra++; }
  for (const r of contact) { const k = r.createdAt.slice(0, 10); if (k in map) map[k].contact++; }
  return Object.entries(map).map(([date, c]) => ({
    date,
    label: new Date(date + "T12:00:00").toLocaleDateString("en-ZA", { day: "numeric", month: "short" }),
    ...c,
    total: c.access + c.infra + c.contact,
  }));
}

// ─── Dashboard Panel ──────────────────────────────────────────────────────────

function DashboardPanel({
  accessRequests, infraRequests, contactRequests, archivedCount, loading, onNavigate,
}: {
  accessRequests:  { createdAt: string }[];
  infraRequests:   { createdAt: string }[];
  contactRequests: { createdAt: string }[];
  archivedCount:   number;
  loading:         boolean;
  onNavigate:      (tab: Tab) => void;
}) {
  const combined = getCombined30Days(accessRequests, infraRequests, contactRequests);
  const total30  = combined.reduce((s, d) => s + d.total, 0);
  const weekly   = combined.slice(-7).reduce((s, d) => s + d.total, 0);
  const dailyAvg = total30 > 0 ? (total30 / 30).toFixed(1) : "0";

  const summaryCards = [
    { label: "Access / NDA",    value: accessRequests.length,  icon: Users,   tab: "access"         as Tab, color: "text-[#7B9FFF]" },
    { label: "Infrastructure",  value: infraRequests.length,   icon: Server,  tab: "infrastructure" as Tab, color: "text-blue-400" },
    { label: "Contact",         value: contactRequests.length, icon: Mail,    tab: "contact"        as Tab, color: "text-[#f08080]" },
    { label: "Archived",        value: archivedCount,          icon: Archive, tab: "archived"       as Tab, color: "text-yellow-400" },
  ];

  return (
    <div className="space-y-5">
      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {summaryCards.map(({ label, value, icon: Icon, tab, color }) => (
          <button
            key={tab}
            onClick={() => onNavigate(tab)}
            className="bg-[#1E2939] border border-white/8 rounded-lg px-4 py-4 text-left hover:border-white/20 hover:bg-[#253347] transition-colors group"
          >
            <div className="flex items-center justify-between mb-2">
              <Icon className={`w-4 h-4 ${color}`} />
              <span className="text-[10px] text-white/30 group-hover:text-white/50 transition-colors uppercase tracking-wide">View →</span>
            </div>
            <p className={`text-2xl font-semibold ${color}`}>{loading ? "—" : value}</p>
            <p className="text-xs text-white/40 mt-0.5">{label}</p>
          </button>
        ))}
      </div>

      {/* Quick stats */}
      <div className="bg-[#1E2939] border border-white/8 rounded-lg p-5">
        <p className="text-xs text-white/30 uppercase tracking-widest mb-3">Activity Overview</p>
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: "This Week",    value: weekly },
            { label: "Last 30 Days", value: total30 },
            { label: "Daily Avg",    value: dailyAvg },
          ].map(({ label, value }) => (
            <div key={label} className="bg-white/5 rounded-lg px-4 py-3">
              <p className="text-xs text-white/35 mb-1 uppercase tracking-wide">{label}</p>
              <p className="text-xl font-semibold text-white">{value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Combined chart */}
      <div className="bg-[#1E2939] border border-white/8 rounded-lg p-5">
        <p className="text-xs text-white/25 mb-4 uppercase tracking-widest">All Requests — Last 30 Days</p>
        <div className="h-44">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={combined} barSize={5} margin={{ top: 0, right: 4, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: "rgba(255,255,255,0.3)", fontSize: 9 }} tickLine={false} axisLine={false} interval={4} />
              <YAxis tick={{ fill: "rgba(255,255,255,0.3)", fontSize: 9 }} tickLine={false} axisLine={false} allowDecimals={false} width={18} />
              <Tooltip contentStyle={{ background: "#151F2E", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 11 }} labelStyle={{ color: "rgba(255,255,255,0.6)" }} itemStyle={{ color: "#fff" }} cursor={{ fill: "rgba(255,255,255,0.03)" }} />
              <Legend wrapperStyle={{ fontSize: 10, color: "rgba(255,255,255,0.4)", paddingTop: 8 }} />
              <Bar dataKey="access"  stackId="a" fill="#7B9FFF" radius={[0, 0, 0, 0]} name="Access / NDA" />
              <Bar dataKey="infra"   stackId="a" fill="#60a5fa" radius={[0, 0, 0, 0]} name="Infrastructure" />
              <Bar dataKey="contact" stackId="a" fill="#f08080" radius={[3, 3, 0, 0]} name="Contact" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Shortcuts */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: "Access / NDA Requests", icon: Users,    tab: "access"         as Tab },
          { label: "Infrastructure",         icon: Server,   tab: "infrastructure" as Tab },
          { label: "Contact Requests",       icon: Mail,     tab: "contact"        as Tab },
          { label: "Archived Records",       icon: Archive,  tab: "archived"       as Tab },
        ].map(({ label, icon: Icon, tab }) => (
          <button
            key={tab}
            onClick={() => onNavigate(tab)}
            className="bg-[#1E2939] border border-white/8 hover:border-[#7B1A1A]/40 hover:bg-[#253347] rounded-lg px-4 py-3 text-left transition-colors flex items-center gap-2.5"
          >
            <Icon className="w-3.5 h-3.5 text-white/40 flex-shrink-0" />
            <span className="text-xs text-white/60 leading-snug">{label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Notifications ────────────────────────────────────────────────────────────

const LS_KEY = "leo_admin_read_v1";

interface Notification {
  id:        string;
  type:      "access" | "infrastructure" | "contact";
  name:      string;
  email:     string;
  createdAt: string;
}

const TYPE_LABEL: Record<Notification["type"], string> = {
  access:         "Access / NDA",
  infrastructure: "Infrastructure",
  contact:        "Contact",
};

const TYPE_COLOR: Record<Notification["type"], string> = {
  access:         "text-[#7B9FFF]",
  infrastructure: "text-blue-400",
  contact:        "text-[#f08080]",
};

const TYPE_TAB: Record<Notification["type"], Tab> = {
  access:         "access",
  infrastructure: "infrastructure",
  contact:        "contact",
};

function useNotifications(
  accessRequests:  AccessRequest[],
  infraRequests:   InfrastructureRequest[],
  contactRequests: ContactRequest[],
) {
  const [readIds, setReadIds] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem(LS_KEY) || "[]")); }
    catch { return new Set(); }
  });

  const notifications: Notification[] = [
    ...accessRequests.map((r) => ({
      id: r.id, type: "access" as const,
      name: `${r.firstName} ${r.lastName}`, email: r.email, createdAt: r.createdAt,
    })),
    ...infraRequests.map((r) => ({
      id: r.id, type: "infrastructure" as const,
      name: r.name, email: r.email, createdAt: r.createdAt,
    })),
    ...contactRequests.map((r) => ({
      id: r.id, type: "contact" as const,
      name: r.name, email: r.email, createdAt: r.createdAt,
    })),
  ].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
   .slice(0, 40);

  const unreadCount = notifications.filter((n) => !readIds.has(n.id)).length;

  function markRead(id: string) {
    setReadIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      try { localStorage.setItem(LS_KEY, JSON.stringify([...next])); } catch { /* noop */ }
      return next;
    });
  }

  function markAllRead() {
    const all = new Set(notifications.map((n) => n.id));
    setReadIds(all);
    try { localStorage.setItem(LS_KEY, JSON.stringify([...all])); } catch { /* noop */ }
  }

  return { notifications, unreadCount, readIds, markRead, markAllRead };
}

function NotificationsPanel({
  notifications,
  unreadCount,
  readIds,
  markRead,
  markAllRead,
  onNavigate,
  onClose,
}: {
  notifications: Notification[];
  unreadCount:   number;
  readIds:       Set<string>;
  markRead:      (id: string) => void;
  markAllRead:   () => void;
  onNavigate:    (tab: Tab) => void;
  onClose:       () => void;
}) {
  function timeAgo(iso: string) {
    const diff = Date.now() - new Date(iso).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 1)  return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  }

  return (
    <div className="absolute right-0 top-full mt-2 w-80 bg-[#0D1520] border border-white/12 rounded-xl shadow-[0_8px_40px_rgba(0,0,0,0.6)] z-[150] overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/8">
        <div className="flex items-center gap-2">
          <Bell className="w-3.5 h-3.5 text-white/40" />
          <span className="text-xs font-semibold text-white/70">Notifications</span>
          {unreadCount > 0 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#7B1A1A]/60 text-white/80 font-medium leading-none">
              {unreadCount}
            </span>
          )}
        </div>
        {unreadCount > 0 && (
          <button onClick={markAllRead} className="text-[10px] text-white/30 hover:text-white/60 transition-colors">
            Mark all read
          </button>
        )}
      </div>

      {/* List */}
      <div className="max-h-80 overflow-y-auto divide-y divide-white/5">
        {notifications.length === 0 && (
          <p className="text-center text-white/25 text-xs py-8">No notifications yet.</p>
        )}
        {notifications.map((n) => {
          const isRead = readIds.has(n.id);
          return (
            <button
              key={n.id}
              onClick={() => { markRead(n.id); onNavigate(TYPE_TAB[n.type]); onClose(); }}
              className={`w-full text-left px-4 py-3 hover:bg-white/4 transition-colors flex items-start gap-3 ${isRead ? "opacity-50" : ""}`}
            >
              {!isRead && <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#7B1A1A] flex-shrink-0" />}
              {isRead  && <span className="mt-1.5 w-1.5 h-1.5 flex-shrink-0" />}
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2 mb-0.5">
                  <span className={`text-[10px] font-semibold uppercase tracking-wide ${TYPE_COLOR[n.type]}`}>
                    {TYPE_LABEL[n.type]}
                  </span>
                  <span className="text-[10px] text-white/25 flex-shrink-0">{timeAgo(n.createdAt)}</span>
                </div>
                <p className="text-xs text-white/80 truncate font-medium">{n.name}</p>
                <p className="text-[10px] text-white/35 truncate">{n.email}</p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Analytics helpers ────────────────────────────────────────────────────────

function getLast30Days(items: { createdAt: string }[]) {
  const counts: Record<string, number> = {};
  const now = new Date();
  for (let i = 29; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    counts[d.toISOString().slice(0, 10)] = 0;
  }
  for (const item of items) {
    const k = item.createdAt.slice(0, 10);
    if (k in counts) counts[k]++;
  }
  return Object.entries(counts).map(([date, count]) => ({
    date,
    label: new Date(date + "T12:00:00").toLocaleDateString("en-ZA", { day: "numeric", month: "short" }),
    count,
  }));
}

// ─── Analytics Panel ──────────────────────────────────────────────────────────

function AnalyticsPanel({ items, label }: { items: { createdAt: string }[]; label: string }) {
  const data     = getLast30Days(items);
  const total30  = data.reduce((s, d) => s + d.count, 0);
  const weekly   = data.slice(-7).reduce((s, d) => s + d.count, 0);
  const dailyAvg = total30 > 0 ? (total30 / 30).toFixed(1) : "0";

  return (
    <div className="p-5 border-b border-white/8 space-y-4">
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: "Last 30 Days", value: total30 },
          { label: "This Week",    value: weekly },
          { label: "Daily Avg",    value: dailyAvg },
        ].map(({ label: l, value }) => (
          <div key={l} className="bg-white/5 rounded-lg px-4 py-3">
            <p className="text-xs text-white/35 mb-1 uppercase tracking-wide">{l}</p>
            <p className="text-xl font-semibold text-white">{value}</p>
          </div>
        ))}
      </div>
      <div>
        <p className="text-xs text-white/25 mb-3 uppercase tracking-widest">{label} — Last 30 Days</p>
        <div className="h-36">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} barSize={7} margin={{ top: 0, right: 4, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fill: "rgba(255,255,255,0.3)", fontSize: 10 }}
                tickLine={false}
                axisLine={false}
                interval={4}
              />
              <YAxis
                tick={{ fill: "rgba(255,255,255,0.3)", fontSize: 10 }}
                tickLine={false}
                axisLine={false}
                allowDecimals={false}
                width={20}
              />
              <Tooltip
                contentStyle={{ background: "#151F2E", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12, padding: "6px 10px" }}
                labelStyle={{ color: "rgba(255,255,255,0.6)" }}
                itemStyle={{ color: "#ffffff" }}
                cursor={{ fill: "rgba(255,255,255,0.03)" }}
              />
              <Bar dataKey="count" fill="#7B1A1A" radius={[3, 3, 0, 0]} name="Requests" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

// ─── Archived Panel ───────────────────────────────────────────────────────────

const typeLabels: Record<string, string> = {
  access:         "Access Request",
  infrastructure: "Infrastructure",
  contact:        "Contact",
};

const typeBadgeClasses: Record<string, string> = {
  access:         "bg-purple-500/15 text-purple-400 border-purple-500/30",
  infrastructure: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  contact:        "bg-[#7B1A1A]/20 text-[#f08080] border-[#7B1A1A]/30",
};

function ArchivedPanel({
  records,
  onDelete,
  loading,
}: {
  records:  ArchivedRecord[];
  onDelete: (id: string, recordType: RecordType) => void;
  loading:  boolean;
}) {
  const [filter, setFilter] = useState<"all" | RecordType>("all");

  const filtered = filter === "all" ? records : records.filter((r) => r._type === filter);

  if (loading && records.length === 0) {
    return <div className="px-6 py-16 text-center text-white/30 text-sm">Loading…</div>;
  }

  return (
    <div>
      {/* Filter bar */}
      <div className="px-5 py-3 border-b border-white/8 flex flex-wrap items-center gap-3">
        <span className="text-xs text-white/40 uppercase tracking-wide">Filter:</span>
        <div className="flex flex-wrap gap-2">
          {(["all", "access", "infrastructure", "contact"] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1 rounded text-xs transition-colors ${
                filter === f
                  ? "bg-[#7B1A1A]/40 text-white border border-[#7B1A1A]/40"
                  : "bg-white/5 text-white/40 border border-white/10 hover:text-white hover:bg-white/8"
              }`}
            >
              {f === "all" ? "All" : typeLabels[f]}
            </button>
          ))}
        </div>
        <span className="ml-auto text-xs text-white/25">
          {filtered.length} record{filtered.length !== 1 ? "s" : ""}
        </span>
      </div>

      {filtered.length === 0 ? (
        <div className="px-6 py-16 text-center text-white/30 text-sm">No archived records.</div>
      ) : (
        <>
          {/* Mobile */}
          <div className="md:hidden divide-y divide-white/5">
            {filtered.map((r) => {
              const name = r._type === "access"
                ? `${(r as AccessRequest).firstName} ${(r as AccessRequest).lastName}`
                : (r as InfrastructureRequest | ContactRequest).name;
              return (
                <div key={r.id} className="p-4 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-white font-medium text-sm">{name}</p>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${typeBadgeClasses[r._type]}`}>
                      {typeLabels[r._type]}
                    </span>
                  </div>
                  <p className="text-white/50 text-xs">{r.email}</p>
                  <div className="flex items-center justify-between pt-1">
                    <p className="text-white/30 text-xs">
                      {new Date(r.createdAt).toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" })}
                    </p>
                    <button
                      onClick={() => {
                        if (window.confirm("Are you sure you want to delete this record?")) {
                          onDelete(r.id, r._type);
                        }
                      }}
                      className="flex items-center gap-1.5 px-2.5 py-1 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 text-left">
                  {["Name", "Email", "Type", "Date", ""].map((h) => (
                    <th key={h} className="px-4 py-3 text-xs font-semibold text-white/30 uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {filtered.map((r) => {
                  const name = r._type === "access"
                    ? `${(r as AccessRequest).firstName} ${(r as AccessRequest).lastName}`
                    : (r as InfrastructureRequest | ContactRequest).name;
                  return (
                    <tr key={r.id} className="hover:bg-white/3 transition-colors">
                      <td className="px-4 py-3 font-medium text-white whitespace-nowrap">{name}</td>
                      <td className="px-4 py-3 text-white/60 text-xs">{r.email}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${typeBadgeClasses[r._type]}`}>
                          {typeLabels[r._type]}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-white/40 whitespace-nowrap text-xs">
                        {new Date(r.createdAt).toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" })}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => {
                            if (window.confirm("Are you sure you want to delete this record?")) {
                              onDelete(r.id, r._type);
                            }
                          }}
                          className="flex items-center gap-1.5 px-2.5 py-1 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10 rounded transition-colors ml-auto"
                        >
                          <Trash2 className="w-3.5 h-3.5" /> Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Security Settings Panel ──────────────────────────────────────────────────

type TotpSetup = { secret: string; qrCode: string } | null;
type SecurityView = "idle" | "setup-qr" | "setup-confirm" | "disable-confirm";

function SecuritySettingsPanel({ onClose }: { onClose: () => void }) {
  const [totpEnabled,   setTotpEnabled]   = useState<boolean | null>(null);
  const [view,          setView]          = useState<SecurityView>("idle");
  const [setupData,     setSetupData]     = useState<TotpSetup>(null);
  const [code,          setCode]          = useState("");
  const [statusMsg,     setStatusMsg]     = useState<string | null>(null);
  const [error,         setError]         = useState<string | null>(null);
  const [loading,       setLoading]       = useState(false);

  useEffect(() => {
    fetch("/api/auth/2fa/status", { credentials: "include" })
      .then((r) => r.json())
      .then((d) => { if (d.success) setTotpEnabled(d.enabled); })
      .catch(() => {});
  }, []);

  async function startSetup() {
    setLoading(true);
    setError(null);
    try {
      const res  = await fetch("/api/auth/2fa/setup", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" } });
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      setSetupData({ secret: data.secret, qrCode: data.qrCode });
      setView("setup-qr");
    } catch (e: unknown) { setError(e instanceof Error ? e.message : "Failed to start setup."); }
    finally { setLoading(false); }
  }

  async function confirmEnable() {
    if (!setupData || code.length !== 6) return;
    setLoading(true);
    setError(null);
    try {
      const res  = await fetch("/api/auth/2fa/enable", {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ secret: setupData.secret, token: code }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      setTotpEnabled(true);
      setView("idle");
      setCode("");
      setSetupData(null);
      setStatusMsg("2FA enabled. Use Google Authenticator on every login.");
    } catch (e: unknown) { setError(e instanceof Error ? e.message : "Verification failed."); setCode(""); }
    finally { setLoading(false); }
  }

  async function confirmDisable() {
    if (code.length !== 6) return;
    setLoading(true);
    setError(null);
    try {
      const res  = await fetch("/api/auth/2fa/disable", {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: code }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      setTotpEnabled(false);
      setView("idle");
      setCode("");
      setStatusMsg("2FA has been disabled.");
    } catch (e: unknown) { setError(e instanceof Error ? e.message : "Verification failed."); setCode(""); }
    finally { setLoading(false); }
  }

  const codeInput = (
    <input
      type="text"
      inputMode="numeric"
      maxLength={6}
      value={code}
      onChange={(e) => { setCode(e.target.value.replace(/\D/g, "").slice(0, 6)); setError(null); }}
      placeholder="000000"
      autoFocus
      className="w-full px-3 py-2.5 rounded bg-[#0D1520] border border-white/10 text-white text-center text-lg font-mono tracking-[0.4em] placeholder-white/20 focus:outline-none focus:ring-2 focus:ring-[#7B1A1A]/50 transition-colors"
    />
  );

  return (
    <div className="absolute right-0 top-full mt-2 w-80 bg-[#0D1520] border border-white/12 rounded-xl z-[150] shadow-2xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/8">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-[#7B1A1A]" />
          <span className="text-sm font-semibold text-white">Security</span>
        </div>
        <button onClick={onClose} className="text-white/40 hover:text-white transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="p-4 space-y-4">

        {statusMsg && (
          <p className="text-xs text-green-400 bg-green-400/10 border border-green-400/20 rounded px-3 py-2">{statusMsg}</p>
        )}
        {error && (
          <p className="text-xs text-red-400 bg-red-400/10 border border-red-400/20 rounded px-3 py-2">{error}</p>
        )}

        {/* Status badge */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-semibold text-white/70 uppercase tracking-wide">Two-Factor Auth</p>
            <p className="text-[10px] text-white/30 mt-0.5">Google Authenticator (TOTP)</p>
          </div>
          {totpEnabled === null ? (
            <span className="text-[10px] text-white/30">Loading…</span>
          ) : (
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${totpEnabled ? "bg-green-500/15 text-green-400" : "bg-white/8 text-white/40"}`}>
              {totpEnabled ? "Enabled" : "Disabled"}
            </span>
          )}
        </div>

        {/* Idle view */}
        {view === "idle" && totpEnabled === false && (
          <button
            onClick={startSetup}
            disabled={loading}
            className="w-full bg-[#7B1A1A] hover:bg-[#6b1515] disabled:opacity-40 text-white py-2 rounded text-sm font-medium transition-colors"
          >
            {loading ? "Generating…" : "Set Up 2FA"}
          </button>
        )}
        {view === "idle" && totpEnabled === true && (
          <button
            onClick={() => { setView("disable-confirm"); setCode(""); setError(null); }}
            className="w-full border border-white/15 hover:border-white/30 text-white/60 hover:text-white py-2 rounded text-sm transition-colors"
          >
            Disable 2FA
          </button>
        )}

        {/* Setup — QR step */}
        {view === "setup-qr" && setupData && (
          <div className="space-y-3">
            <p className="text-xs text-white/50 leading-relaxed">
              Scan this QR code with <strong className="text-white/70">Google Authenticator</strong>, then click Continue.
            </p>
            <div className="flex justify-center bg-white rounded-lg p-3">
              <img src={setupData.qrCode} alt="2FA QR Code" className="w-40 h-40" />
            </div>
            <p className="text-[10px] text-white/30 text-center break-all font-mono">{setupData.secret}</p>
            <div className="flex gap-2">
              <button onClick={() => setView("idle")} className="flex-1 border border-white/15 text-white/50 py-2 rounded text-xs hover:bg-white/5 transition-colors">Cancel</button>
              <button onClick={() => { setView("setup-confirm"); setCode(""); setError(null); }} className="flex-1 bg-[#7B1A1A] hover:bg-[#6b1515] text-white py-2 rounded text-xs font-medium transition-colors">Continue →</button>
            </div>
          </div>
        )}

        {/* Setup — confirm code */}
        {view === "setup-confirm" && (
          <div className="space-y-3">
            <p className="text-xs text-white/50">Enter the 6-digit code from your authenticator app to confirm setup.</p>
            {codeInput}
            <div className="flex gap-2">
              <button onClick={() => setView("setup-qr")} className="flex-1 border border-white/15 text-white/50 py-2 rounded text-xs hover:bg-white/5 transition-colors">← Back</button>
              <button onClick={confirmEnable} disabled={loading || code.length !== 6} className="flex-1 bg-[#7B1A1A] hover:bg-[#6b1515] disabled:opacity-40 text-white py-2 rounded text-xs font-medium transition-colors">{loading ? "Verifying…" : "Enable 2FA"}</button>
            </div>
          </div>
        )}

        {/* Disable — confirm code */}
        {view === "disable-confirm" && (
          <div className="space-y-3">
            <p className="text-xs text-white/50">Enter your current authenticator code to disable 2FA.</p>
            {codeInput}
            <div className="flex gap-2">
              <button onClick={() => setView("idle")} className="flex-1 border border-white/15 text-white/50 py-2 rounded text-xs hover:bg-white/5 transition-colors">Cancel</button>
              <button onClick={confirmDisable} disabled={loading || code.length !== 6} className="flex-1 border border-red-500/30 text-red-400 hover:bg-red-500/10 disabled:opacity-40 py-2 rounded text-xs font-medium transition-colors">{loading ? "Verifying…" : "Disable 2FA"}</button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────

export function AdminPage() {
  const navigate = useNavigate();

  const [accessRequests,  setAccessRequests]  = useState<AccessRequest[]>([]);
  const [contactRequests, setContactRequests] = useState<ContactRequest[]>([]);
  const [infraRequests,   setInfraRequests]   = useState<InfrastructureRequest[]>([]);
  const [tab,             setTab]             = useState<Tab>("dashboard");
  const [loading,           setLoading]           = useState(false);
  const [actionLoading,     setActionLoading]     = useState<string | null>(null);
  const [accessLinks,       setAccessLinks]       = useState<Record<string, string>>({});
  const [error,           setError]           = useState<string | null>(null);
  const [sidebarOpen,     setSidebarOpen]     = useState(false);
  const [requestsOpen,    setRequestsOpen]    = useState(true);
  const [isoOpen,         setIsoOpen]         = useState(false);
  const [panelRecord,      setPanelRecord]     = useState<AccessRequest | null>(null);
  const [leadPanelRecord,  setLeadPanelRecord] = useState<LeadPanelRecord | null>(null);
  const [notifOpen,        setNotifOpen]       = useState(false);
  const [securityOpen,     setSecurityOpen]    = useState(false);
  const notifRef    = useRef<HTMLDivElement>(null);
  const securityRef = useRef<HTMLDivElement>(null);

  const handleUnauthorized = useCallback(() => { navigate("/admin-login"); }, [navigate]);

  const { notifications, unreadCount, readIds, markRead, markAllRead } = useNotifications(
    accessRequests, infraRequests, contactRequests
  );

  useEffect(() => {
    if (!notifOpen) return;
    const handler = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) setNotifOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [notifOpen]);

  useEffect(() => {
    if (!securityOpen) return;
    const handler = (e: MouseEvent) => {
      if (securityRef.current && !securityRef.current.contains(e.target as Node)) setSecurityOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [securityOpen]);


  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [reqRes, contactRes, infraRes] = await Promise.all([
        fetch("/api/admin/requests",                { credentials: "include" }),
        fetch("/api/admin/contact-requests",        { credentials: "include" }),
        fetch("/api/admin/infrastructure-requests", { credentials: "include" }),
      ]);
      if (reqRes.status === 401) { handleUnauthorized(); return; }
      const [reqData, contactData, infraData] = await Promise.all([
        reqRes.json(), contactRes.json(), infraRes.json(),
      ]);
      if (!reqData.success) throw new Error(reqData.message);
      setAccessRequests(reqData.data);
      if (contactData.success) setContactRequests(contactData.data);
      if (infraData.success)   setInfraRequests(infraData.data);
    } catch {
      setError("Failed to load requests.");
    } finally {
      setLoading(false);
    }
  }, [handleUnauthorized]);

  useEffect(() => {
    fetchRequests();
    const poll = setInterval(fetchRequests, 30000);
    return () => clearInterval(poll);
  }, [fetchRequests]);

  const approve = async (id: string) => {
    setActionLoading(id + "-approve");
    try {
      const res = await fetch("/api/admin/approve", {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id }),
      });
      if (res.status === 401) { handleUnauthorized(); return; }
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      setAccessLinks((p) => ({ ...p, [id]: data.accessLink }));
      await fetchRequests();
    } catch { setError("Failed to approve request."); }
    finally  { setActionLoading(null); }
  };

  const reject = async (id: string) => {
    setActionLoading(id + "-reject");
    try {
      const res = await fetch("/api/admin/reject", {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id }),
      });
      if (res.status === 401) { handleUnauthorized(); return; }
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      await fetchRequests();
    } catch { setError("Failed to reject request."); }
    finally  { setActionLoading(null); }
  };

  const archive = async (id: string) => {
    setActionLoading(id + "-archive");
    try {
      const res = await fetch("/api/admin/archive", {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id }),
      });
      if (res.status === 401) { handleUnauthorized(); return; }
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      await fetchRequests();
    } catch { setError("Failed to archive request."); }
    finally  { setActionLoading(null); }
  };

  const setStage = async (id: string, recordType: string, stage: Stage) => {
    try {
      const res = await fetch("/api/admin/set-stage", {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id, recordType, stage }),
      });
      if (res.status === 401) { handleUnauthorized(); return; }
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      await fetchRequests();
    } catch { setError("Failed to update stage."); }
  };

  const archiveRecord = async (id: string, recordType: string) => {
    try {
      const res = await fetch("/api/admin/archive-record", {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id, recordType }),
      });
      if (res.status === 401) { handleUnauthorized(); return; }
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      await fetchRequests();
    } catch { setError("Failed to archive record."); }
  };

  const deleteRecord = async (id: string, recordType: RecordType) => {
    try {
      const res = await fetch("/api/admin/delete-record", {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id, recordType }),
      });
      if (res.status === 401) { handleUnauthorized(); return; }
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      await fetchRequests();
    } catch { setError("Failed to delete record."); }
  };

  const extendSession = async (id: string) => {
    try {
      const res = await fetch(`/api/admin/requests/${id}/extend-session`, {
        method: "POST", credentials: "include",
      });
      if (res.status === 401) { handleUnauthorized(); return; }
      const data = await res.json();
      if (!data.success) throw new Error(data.message);
      await fetchRequests();
    } catch { setError("Failed to extend session."); }
  };

  const signOut = () => {
    fetch("/api/auth/logout", { method: "POST", credentials: "include" })
      .finally(() => navigate("/admin-login"));
  };

  const active        = accessRequests.filter((r) => !r.archived);
  const activeInfra   = infraRequests.filter((r)  => !r.archived);
  const activeContact = contactRequests.filter((r) => !r.archived);

  const archivedAll: ArchivedRecord[] = [
    ...accessRequests.filter((r)  => r.archived).map((r) => ({ ...r, _type: "access"         as const })),
    ...infraRequests.filter((r)   => r.archived).map((r) => ({ ...r, _type: "infrastructure" as const })),
    ...contactRequests.filter((r) => r.archived).map((r) => ({ ...r, _type: "contact"        as const })),
  ];

  const counts = {
    pending:  active.filter((r) => r.status === "pending").length,
    approved: active.filter((r) => r.status === "approved").length,
    rejected: active.filter((r) => r.status === "rejected").length,
  };

  const requestItems: { key: Tab; label: string; icon: React.ElementType; count?: number }[] = [
    { key: "access",         label: "Access / NDA Requests", icon: Users,   count: active.length },
    { key: "infrastructure", label: "Infrastructure",  icon: Server,  count: activeInfra.length },
    { key: "contact",        label: "Contact",         icon: Mail,    count: activeContact.length },
    { key: "archived",       label: "Archived",        icon: Archive, count: archivedAll.length },
  ];

  const isoItems: { key: Tab; label: string; icon: React.ElementType }[] = [
    { key: "iso-dashboard", label: "Dashboard",     icon: BarChart2 },
    { key: "iso-documents", label: "Documents",     icon: BookOpen },
    { key: "iso-controls",  label: "Controls",      icon: ShieldCheck },
    { key: "iso-risks",     label: "Risk Register", icon: AlertTriangle },
    { key: "iso-incidents", label: "Incidents",     icon: AlertCircle },
    { key: "iso-audits",    label: "Audits",        icon: ClipboardList },
  ];

  const anyChildActive = requestItems.some((i) => i.key === tab && tab !== "dashboard");
  const anyIsoActive   = isoItems.some((i) => i.key === tab);

  const SidebarContent = () => (
    <>
      <div className="flex flex-col items-center px-4 py-7 border-b border-white/8">
        <Logo className="w-20 h-auto" white />
        <p className="text-[10px] text-white/25 mt-3 font-semibold uppercase tracking-[0.18em]">Admin Portal</p>
      </div>

      <nav className="flex-1 p-3 pt-4 space-y-0.5">
        <button
          onClick={() => { setTab("dashboard"); setSidebarOpen(false); }}
          className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition-colors ${
            tab === "dashboard"
              ? "bg-[#7B1A1A]/30 text-white border border-[#7B1A1A]/30"
              : "text-white/50 hover:text-white hover:bg-white/5"
          }`}
        >
          <LayoutDashboard className="w-4 h-4 flex-shrink-0" />
          <span className="font-medium">Dashboard</span>
        </button>

        <button
          onClick={() => setRequestsOpen((v) => !v)}
          className={`w-full flex items-center justify-between gap-2.5 px-3 py-2.5 rounded-lg text-sm transition-colors ${
            anyChildActive
              ? "text-white bg-white/5"
              : "text-white/50 hover:text-white hover:bg-white/5"
          }`}
        >
          <span className="flex items-center gap-2.5">
            <Inbox className="w-4 h-4 flex-shrink-0" />
            <span className="font-medium">Requests</span>
          </span>
          <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${requestsOpen ? "rotate-180" : ""}`} />
        </button>

        {requestsOpen && (
          <div className="ml-3 pl-3 border-l border-white/8 space-y-0.5 mt-0.5">
            {requestItems.map(({ key, label, icon: Icon, count }) => (
              <button
                key={key}
                onClick={() => { setTab(key); setSidebarOpen(false); }}
                className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                  tab === key
                    ? "bg-[#7B1A1A]/30 text-white border border-[#7B1A1A]/30"
                    : "text-white/45 hover:text-white hover:bg-white/5"
                }`}
              >
                <span className="flex items-center gap-2">
                  <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                  {label}
                </span>
                {count !== undefined && count > 0 && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium leading-none ${
                    tab === key ? "bg-[#7B1A1A]/60 text-white/80" : "bg-white/10 text-white/35"
                  }`}>
                    {count}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        <button
          onClick={() => setIsoOpen((v) => !v)}
          className={`w-full flex items-center justify-between gap-2.5 px-3 py-2.5 rounded-lg text-sm transition-colors ${
            anyIsoActive
              ? "text-white bg-white/5"
              : "text-white/50 hover:text-white hover:bg-white/5"
          }`}
        >
          <span className="flex items-center gap-2.5">
            <ShieldAlert className="w-4 h-4 flex-shrink-0" />
            <span className="font-medium">ISO 27001</span>
          </span>
          <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${isoOpen ? "rotate-180" : ""}`} />
        </button>

        {isoOpen && (
          <div className="ml-3 pl-3 border-l border-white/8 space-y-0.5 mt-0.5">
            {isoItems.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => { setTab(key); setSidebarOpen(false); }}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                  tab === key
                    ? "bg-[#7B1A1A]/30 text-white border border-[#7B1A1A]/30"
                    : "text-white/45 hover:text-white hover:bg-white/5"
                }`}
              >
                <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                {label}
              </button>
            ))}
          </div>
        )}
      </nav>

      <div className="p-3 border-t border-white/8">
        <button
          onClick={signOut}
          className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm text-white/35 hover:text-white hover:bg-white/5 transition-colors"
        >
          <LogOut className="w-4 h-4" />
          Sign Out
        </button>
      </div>
    </>
  );

  return (
    <div className="flex h-screen bg-[#101828] text-white overflow-hidden">
      <AccessActionsPanel
        record={panelRecord}
        onClose={() => setPanelRecord(null)}
        approve={approve}
        reject={reject}
        archive={archive}
        extendSession={extendSession}
        accessLinks={accessLinks}
        onViewNDA={(id) => navigate(`/admin/nda-view/${id}`)}
        actionLoading={actionLoading}
      />
      <LeadActionsPanel
        record={leadPanelRecord}
        onClose={() => setLeadPanelRecord(null)}
        onSetStage={setStage}
        onArchive={archiveRecord}
      />

      {/* Sidebar — desktop */}
      <aside className="hidden md:flex flex-col w-56 bg-[#0D1520] border-r border-white/8 flex-shrink-0">
        <SidebarContent />
      </aside>

      {/* Sidebar — mobile overlay */}
      {sidebarOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="w-56 bg-[#0D1520] border-r border-white/8 flex flex-col">
            <div className="flex justify-end p-3">
              <button onClick={() => setSidebarOpen(false)} className="p-1.5 text-white/50 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
            <SidebarContent />
          </div>
          <div className="flex-1 bg-black/50" onClick={() => setSidebarOpen(false)} />
        </div>
      )}

      {/* Main area */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">

        {/* Top bar */}
        <header className="flex-shrink-0 border-b border-white/8 px-4 sm:px-6 py-3.5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button onClick={() => setSidebarOpen(true)} className="md:hidden p-1.5 text-white/50 hover:text-white">
              <Menu className="w-5 h-5" />
            </button>
            <div>
              <p className="text-sm font-semibold text-white">
                {tab === "dashboard"
                  ? "Dashboard"
                  : anyIsoActive
                    ? `ISO 27001 — ${isoItems.find((i) => i.key === tab)?.label ?? ""}`
                    : requestItems.find((n) => n.key === tab)?.label}
              </p>
              <p className="text-xs text-white/30 hidden sm:block">LEO Group Admin Dashboard</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={fetchRequests}
              disabled={loading}
              className="flex items-center gap-1.5 text-xs text-white/40 hover:text-white transition-colors"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
              <span className="hidden sm:inline">Refresh</span>
            </button>

            {/* Open Secure Portal */}
            <a
              href="/api/admin/portal-bypass"
              target="_blank"
              rel="noreferrer"
              title="Open Secure Portal"
              className="flex items-center gap-1.5 text-xs text-white/40 hover:text-white transition-colors border border-white/10 hover:border-white/25 rounded px-2.5 py-1"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Secure Portal</span>
            </a>

            {/* Security */}
            <div ref={securityRef} className="relative">
              <button
                onClick={() => { setSecurityOpen((v) => !v); setNotifOpen(false); }}
                className="relative p-1.5 text-white/40 hover:text-white transition-colors rounded-lg hover:bg-white/6"
                title="Security Settings"
              >
                <Shield className="w-4 h-4" />
              </button>
              {securityOpen && (
                <SecuritySettingsPanel onClose={() => setSecurityOpen(false)} />
              )}
            </div>

            {/* Bell */}
            <div ref={notifRef} className="relative">
              <button
                onClick={() => setNotifOpen((v) => !v)}
                className="relative p-1.5 text-white/40 hover:text-white transition-colors rounded-lg hover:bg-white/6"
              >
                <Bell className="w-4 h-4" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 flex items-center justify-center rounded-full bg-[#7B1A1A] text-white text-[9px] font-bold px-1 leading-none">
                    {unreadCount > 99 ? "99+" : unreadCount}
                  </span>
                )}
              </button>
              {notifOpen && (
                <NotificationsPanel
                  notifications={notifications}
                  unreadCount={unreadCount}
                  readIds={readIds}
                  markRead={markRead}
                  markAllRead={markAllRead}
                  onNavigate={(t) => { setTab(t); }}
                  onClose={() => setNotifOpen(false)}
                />
              )}
            </div>
          </div>
        </header>

        {/* Scrollable content */}
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-5">

          {/* Dashboard panel */}
          {tab === "dashboard" && (
            <DashboardPanel
              accessRequests={active}
              infraRequests={activeInfra}
              contactRequests={activeContact}
              archivedCount={archivedAll.length}
              loading={loading}
              onNavigate={(t) => setTab(t)}
            />
          )}

          {/* Stats — access tab only */}
          {tab === "access" && (
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: "Pending",  value: counts.pending,  color: "text-yellow-400", icon: Clock },
                { label: "Approved", value: counts.approved, color: "text-green-400",  icon: CheckCircle },
                { label: "Rejected", value: counts.rejected, color: "text-red-400",    icon: XCircle },
              ].map(({ label, value, color, icon: Icon }) => (
                <div key={label} className="bg-[#1E2939] border border-white/8 rounded-lg px-4 py-3">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Icon className={`w-3.5 h-3.5 ${color}`} />
                    <span className="text-xs text-white/40 uppercase tracking-wide">{label}</span>
                  </div>
                  <p className={`text-xl font-semibold ${color}`}>{value}</p>
                </div>
              ))}
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="bg-red-900/20 border border-red-500/20 rounded-lg px-4 py-3 text-sm text-red-300">
              {error}
            </div>
          )}

          {/* ISO Module */}
          {anyIsoActive && (
            <ISOModule activeTab={tab.replace("iso-", "") as ISOTab} />
          )}

          {/* Content panel */}
          {tab !== "dashboard" && !anyIsoActive && <div className="bg-[#1E2939] border border-white/8 rounded-lg overflow-hidden">

            {/* Access Requests */}
            {tab === "access" && (
              <>
                <AnalyticsPanel items={active} label="Access / NDA Requests" />
                <AccessTable
                  rows={active}
                  loading={loading}
                  emptyLabel="No active access requests."
                  onOpenActions={(r) => setPanelRecord(r)}
                />
              </>
            )}

            {/* Archived */}
            {tab === "archived" && (
              <ArchivedPanel
                records={archivedAll}
                onDelete={deleteRecord}
                loading={loading}
              />
            )}

            {/* Infrastructure */}
            {tab === "infrastructure" && (
              loading && activeInfra.length === 0 ? (
                <div className="px-6 py-16 text-center text-white/30 text-sm">Loading…</div>
              ) : (
                <>
                  <AnalyticsPanel items={activeInfra} label="Infrastructure Requests" />
                  {activeInfra.length === 0 ? (
                    <div className="px-6 py-16 text-center text-white/30 text-sm">No infrastructure requests yet.</div>
                  ) : (
                    <>
                      <div className="md:hidden divide-y divide-white/5">
                        {activeInfra.map((r) => (
                          <div key={r.id} className="p-4 space-y-3">
                            <div className="flex items-start justify-between gap-2">
                              <p className="text-white font-medium text-sm">{r.name}</p>
                              <StageBadge stage={r.statusStage} />
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              <div><p className="text-white/40 mb-0.5">Email</p><p className="text-white/80 break-all">{r.email}</p></div>
                              <div><p className="text-white/40 mb-0.5">Company</p><p className="text-white/80">{r.company || "—"}</p></div>
                              <div><p className="text-white/40 mb-0.5">Type</p><p className="text-white/80">{r.type || "—"}</p></div>
                              <div><p className="text-white/40 mb-0.5">Date</p><p className="text-white/60">{new Date(r.createdAt).toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" })}</p></div>
                            </div>
                            {r.message && <div><p className="text-white/40 text-xs mb-0.5">Message</p><p className="text-white/60 text-xs">{r.message}</p></div>}
                            <button
                              onClick={() => setLeadPanelRecord({ ...r, _type: "infrastructure" })}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/8 hover:bg-white/12 border border-white/15 text-white/80 text-xs rounded transition-colors"
                            >
                              Actions <ChevronDown className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                      <div className="hidden md:block overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-white/10 text-left">
                              {["Name", "Company", "Email", "Phone", "Type", "Message", "Stage", "Date", ""].map((h) => (
                                <th key={h} className="px-4 py-3 text-xs font-semibold text-white/30 uppercase tracking-wide whitespace-nowrap">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-white/5">
                            {activeInfra.map((r) => (
                              <tr key={r.id} className="hover:bg-white/3 transition-colors">
                                <td className="px-4 py-3 font-medium text-white whitespace-nowrap">{r.name}</td>
                                <td className="px-4 py-3 text-white/60 text-xs">{r.company || "—"}</td>
                                <td className="px-4 py-3 text-white/60 text-xs">{r.email}</td>
                                <td className="px-4 py-3 text-white/60 text-xs whitespace-nowrap">{r.phone || "—"}</td>
                                <td className="px-4 py-3 text-white/60 text-xs">{r.type || "—"}</td>
                                <td className="px-4 py-3 text-white/60 text-xs max-w-[200px] truncate">{r.message || "—"}</td>
                                <td className="px-4 py-3"><StageBadge stage={r.statusStage} /></td>
                                <td className="px-4 py-3 text-white/40 whitespace-nowrap text-xs">{new Date(r.createdAt).toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" })}</td>
                                <td className="px-4 py-3 text-right">
                                  <button
                                    onClick={() => setLeadPanelRecord({ ...r, _type: "infrastructure" })}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/8 hover:bg-white/12 border border-white/15 text-white/80 text-xs rounded transition-colors whitespace-nowrap"
                                  >
                                    Actions <ChevronDown className="w-3 h-3" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </>
              )
            )}

            {/* Contact */}
            {tab === "contact" && (
              loading && activeContact.length === 0 ? (
                <div className="px-6 py-16 text-center text-white/30 text-sm">Loading…</div>
              ) : (
                <>
                  <AnalyticsPanel items={activeContact} label="Contact Requests" />
                  {activeContact.length === 0 ? (
                    <div className="px-6 py-16 text-center text-white/30 text-sm">No contact requests yet.</div>
                  ) : (
                    <>
                      <div className="md:hidden divide-y divide-white/5">
                        {activeContact.map((r) => (
                          <div key={r.id} className="p-4 space-y-3">
                            <div className="flex items-start justify-between gap-2">
                              <p className="text-white font-medium text-sm">{r.name}</p>
                              <StageBadge stage={r.statusStage} />
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs">
                              <div><p className="text-white/40 mb-0.5">Email</p><p className="text-white/80 break-all">{r.email}</p></div>
                              <div><p className="text-white/40 mb-0.5">Company</p><p className="text-white/80">{r.company || "—"}</p></div>
                              <div><p className="text-white/40 mb-0.5">Date</p><p className="text-white/60">{new Date(r.createdAt).toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" })}</p></div>
                            </div>
                            {r.message && <div><p className="text-white/40 text-xs mb-0.5">Message</p><p className="text-white/60 text-xs">{r.message}</p></div>}
                            <button
                              onClick={() => setLeadPanelRecord({ ...r, _type: "contact" })}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/8 hover:bg-white/12 border border-white/15 text-white/80 text-xs rounded transition-colors"
                            >
                              Actions <ChevronDown className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                      <div className="hidden md:block overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-white/10 text-left">
                              {["Name", "Company", "Email", "Service", "NDA", "Message", "Stage", "Date", ""].map((h) => (
                                <th key={h} className="px-4 py-3 text-xs font-semibold text-white/30 uppercase tracking-wide whitespace-nowrap">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-white/5">
                            {activeContact.map((r) => (
                              <tr key={r.id} className="hover:bg-white/3 transition-colors">
                                <td className="px-4 py-3 font-medium text-white whitespace-nowrap">{r.name}</td>
                                <td className="px-4 py-3 text-white/60 text-xs">{r.company || "—"}</td>
                                <td className="px-4 py-3 text-white/60 text-xs">{r.email}</td>
                                <td className="px-4 py-3">
                                  <span className="text-xs bg-[#7B1A1A]/20 text-[#f08080] border border-[#7B1A1A]/30 px-2 py-0.5 rounded-full whitespace-nowrap">
                                    {r.service || "General Inquiry"}
                                  </span>
                                </td>
                                <td className="px-4 py-3">
                                  <span className={`text-xs font-medium ${r.ndaAccepted ? "text-green-400" : "text-yellow-400"}`}>
                                    {r.ndaAccepted ? "Yes" : "No"}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-white/60 text-xs max-w-[260px] truncate">{r.message}</td>
                                <td className="px-4 py-3"><StageBadge stage={r.statusStage} /></td>
                                <td className="px-4 py-3 text-white/40 whitespace-nowrap text-xs">{new Date(r.createdAt).toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" })}</td>
                                <td className="px-4 py-3 text-right">
                                  <button
                                    onClick={() => setLeadPanelRecord({ ...r, _type: "contact" })}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/8 hover:bg-white/12 border border-white/15 text-white/80 text-xs rounded transition-colors whitespace-nowrap"
                                  >
                                    Actions <ChevronDown className="w-3 h-3" />
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </>
              )
            )}

          </div>}
        </main>
      </div>
    </div>
  );
}
